import { PropsWithChildren, useEffect, useRef } from 'react';
import { SESSION_REMINDER_INTERVAL } from '@apps/config';
import { Dialog, DialogRefProps } from '@crudx/mui';
import { useSessionReminderQuery } from '@stack/data-access-clinic';
import { redirect } from '@webbyx/next-js';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { isExcluded } from './helpers';

export const SessionReminderProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props;
  // ================ HOOKS
  const { data: overtimeSessions, startPolling } = useSessionReminderQuery();
  const { t } = useTranslation();
  const modalRef = useRef<DialogRefProps>(null);
  const router = useRouter();

  // ================ EVENTS
  const handlePoll = () => {
    const count = overtimeSessions?.SessionReminder?.pending_session;
    if (count && count > 0) {
      const pathname = router.pathname;
      if (isExcluded(pathname)) {
        return;
      }
      modalRef.current?.open();
    }
  };

  // ================ USE EFFECTS
  // useEffect to start polling
  useEffect(() => {
    startPolling(SESSION_REMINDER_INTERVAL);
  }, []);

  // useEffect to handle poll on poll interval
  useEffect(() => {
    const interval = setInterval(() => {
      handlePoll();
    }, SESSION_REMINDER_INTERVAL);

    return () => {
      clearInterval(interval); // Clear interval on component unmount
    };
  });

  // ================ VIEWS
  return (
    <>
      {children}
      <Dialog
        ref={modalRef}
        type="custom"
        title={t('common:checkOutReminder')}
        message={t('common:checkOutReminderMessage', {
          count: overtimeSessions?.SessionReminder?.pending_session,
        })}
        titleProps={{ sx: { fontSize: '1.25rem', fontWeight: 600 } }}
        messageProps={{ sx: { fontSize: '1rem', color: 'text.secondary' } }}
        onClickPrimaryAction={() => {
          redirect(
            {},
            '/session?session_over_3_hours=true&session_status_slug=ongoing',
            'push'
          );
        }}
        onClickSecondaryAction={() => modalRef.current?.close()}
        actions={[
          {
            type: 'secondary',
            label: t('common:cancel'),
            props: {
              size: 'small',
              variant: 'outlined',
            },
          },
          {
            type: 'primary',
            label: t('common:viewSession'),
            props: {
              size: 'small',
              variant: 'contained',
            },
          },
        ]}
      />
    </>
  );
};

export default SessionReminderProvider;
